<template>
  <div class="rd-screen">
    <report-tools :copy="sections.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!sections.length"
      icon="mdi-truck-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>
    <company-desktop-taken-table
      v-else
      :sections="sections"
      :shifts="visibleShifts"
      :name-label="$t('meal')"
      :show-taken="canCheckReport"
    ></company-desktop-taken-table>
  </div>
</template>

<script>
import CompanyReportTabTaken from "@/components/company/reports/companyTabs/CompanyReportTabTaken.vue";
import hasBarShifts from "@/components/report/shared/hasBarShifts";
import hasShiftSubtitle from "@/components/report/shared/hasShiftSubtitle";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import { hasPeriodRefetch } from "../../hasPeriodRefetch";
import { takenKpis, toTakenRow, toTakenSection, visibleTakenShifts } from "../../takenRows";
import CompanyDesktopTakenTable from "../CompanyDesktopTakenTable.vue";

/**
 * Preuzeto firme na računaru: po firmi obroci sa preuzeto / poručeno po smenama (firma koja ne sme da vidi
 * preuzimanje dobija samo poručene količine). Firme, smene, period i pretragu čita iz zajedničke trake.
 * Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopTaken",
  extends: CompanyReportTabTaken,
  mixins: [hasBarShifts, hasShiftSubtitle, hasPeriodRefetch("fetchMeals")],
  components: { ReportState, ReportTools, CompanyDesktopTakenTable },
  computed: {
    /** Odeljak po firmi sa obrocima po abecedi. */
    sections() {
      const search = this.search;
      return this.filteredCompanies
        .map((company, index) => {
          const found = search ? this.customSearch(company.meals, [{ value: "name" }], search) : company.meals;
          const rows = found
            .filter((meal) => meal.total > 0)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((meal, mealIndex) => toTakenRow(meal, `${index}-${meal.id || meal.name}-${mealIndex}`, meal.name));
          return toTakenSection(`${index}-${company.name}`, this.toLatin(company.name), rows);
        })
        .filter((section) => section.rows.length);
    },
    visibleShifts() {
      return visibleTakenShifts(this.selectedShifts, this.sections);
    },
    emptyTitle() {
      return this.search
        ? this.$t("report_mobile.no_results_for", { search: this.search })
        : this.$t("no_taken_meals_data");
    },
    emptyText() {
      return this.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return takenKpis((key) => this.$t(key), {
        sections: this.sections,
        showTaken: this.canCheckReport,
        countLabel: this.$t("companies"),
        count: this.sections.length,
      });
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.sections.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const headers = [
        this.$t("firm"),
        this.$t("meal"),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("total"),
      ];
      const value = (ordered, taken) => (this.canCheckReport ? `${taken}/${ordered}` : String(ordered));
      const rows = this.sections.flatMap((section) =>
        section.rows.map((row) => [
          section.name,
          row.name,
          ...this.visibleShifts.map((shift) => {
            const cell = row.cells[shift.id];
            return cell.ordered ? value(cell.ordered, cell.taken) : "";
          }),
          value(row.ordered, row.taken),
        ])
      );
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
